/**
 * Tiny hook that sets `document.title` while the component is
 * mounted and restores the previous title on unmount.
 *
 * Enterprise admin consoles with deep drill-ins — "tenants > acme >
 * users > alice@example.com > sessions" — generate long-lived
 * browser history. A consistent, scannable title makes back-button
 * navigation reliable and keeps pinned tabs meaningful.
 *
 * Convention:
 *   useDocumentTitle(["Users", tenant.name])
 *   → "Users · Acme · PureID"
 *
 * The hook accepts either a single string or an array; array parts
 * are joined with ` · ` and the app suffix is appended
 * automatically.
 */

import { useEffect } from "react";

const APP_SUFFIX = "PureID";

export function useDocumentTitle(parts: string | readonly string[]): void {
  // Compute the final title in render, so the effect depends on a single stable string — no
  // complex dep expression and no stale-`parts` closure.
  const segments = Array.isArray(parts) ? parts : [parts];
  const cleaned = segments.map((s) => String(s).trim()).filter((s): s is string => s.length > 0);
  const title = cleaned.length > 0 ? `${cleaned.join(" · ")} · ${APP_SUFFIX}` : APP_SUFFIX;
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
